import React from 'react';
import {
  Copy,
  CheckCircle2,
  FileText,
  Info,
  Bitcoin,
  Lock,
  History,
} from 'lucide-react';

import {
  Badge,
  Box,
  Button,
  Code,
  Container,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';

import { useTranslation } from 'react-i18next';

import useBadgeLink from "./badgeProvider";
import useUser from "./userProvider";

function ShieldBadge({ leftIcon, leftText, rightText, rightColor }) {
  return (
    <Group gap={0} wrap="nowrap">
      <Badge variant="filled" color="dark" leftSection={leftIcon} radius="sm">
        {leftText}
      </Badge>
      <Badge variant="filled" color={rightColor} radius="sm">
        {rightText}
      </Badge>
    </Group>
  );
}

function PreviewBadge({title, image, link}) {
  return (
    <Group gap={0} wrap="nowrap">
      <a href={link}><img src={image} alt={title} /></a>
    </Group>
  )
}

const BadgeSetupWidget = ({params}) => {
  const { t } = useTranslation();
  const {user, repo} = params;
  const {userInfo} = useUser();
  const {data: badgeLink, error} = useBadgeLink(user, repo);

   if ((userInfo == null) && (error?.status === 403)) {
    const url = new URL("/api/login", document.location);
    const params = new URLSearchParams({cont: document.location});
    url.search = params.toString();
    document.location = url.toString();
    return;
    }


  var markdownSnippet = "";
  var preview = null;

  if (badgeLink) {
    markdownSnippet = "<!-- Timestamp GIT Verification Badges -->\n";

    Object.entries(badgeLink).forEach(([key, value]) => {
      markdownSnippet += `${value.markdown}\n`;
      });

    preview = Object.entries(badgeLink).map(([key, value]) => {
      return <PreviewBadge key={key}
                           title={value.linkTitle}
                           image={value.imageUrl}
                           link={value.linkUrl}
      />

    });
  }
  return (
    <Box mih="100vh">
      <Container size="md" py="xl">
        <Paper radius="lg" withBorder shadow="md">
          <Stack p="xl" gap="xl">
            <Stack align="center" ta="center" gap="sm">
              <ThemeIcon color="teal" variant="light" size={64} radius="xl">
                <CheckCircle2 size={32} />
              </ThemeIcon>
              <Title order={2}>
                {t('Repository Connected!')}
              </Title>
              <Text c="dimmed" size="lg" maw={500}>
                {t('We are now actively tracking ')}
                <Text component="span" fw={700}>
                  acme-corp/super-awesome-ai
                </Text>
                .{' '}
                {t('Your first cryptographic anchor is pending in the daily batch.')}
              </Text>
            </Stack>

            <Stack gap="md">
              <Group gap="sm">
                <Badge variant="filled" color="teal" radius="xl">
                  1
                </Badge>
                <Text fw={700} fz="lg">
                  {t('IP Protection Badge')}
                </Text>
              </Group>

              <Text c="dimmed">
                {t('Let your users know this project is cryptographically timestamped. Copy the Markdown below and paste it at the top of your ')}
                <Code>README.md</Code>{' '}
                {t('file.')}
              </Text>

              <Paper withBorder p="md" radius="md">
                <Stack gap="xs">
                  <Text fz="xs" tt="uppercase" fw={700} c="dimmed">
                    {t('Live Preview')}
                  </Text>

                  <Group gap="xs" wrap="wrap">
                    {preview}

                  </Group>
                </Stack>
              </Paper>

              <Paper withBorder radius="md">
                <Group p="sm" px="md" justify="space-between">
                  <Group gap="xs">
                    <FileText size={14} />
                    <Text fz="sm">README.md</Text>
                  </Group>
                  <CopyButton value={markdownSnippet} timeout={2000}>
                    {({ copied, copy }) => (
                      <Button
                        size="xs"
                        variant={copied ? 'light' : 'default'}
                        color={copied ? 'teal' : 'gray'}
                        leftSection={copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                        onClick={copy}
                      >
                        {copied
                          ? t('Copied!')
                          : t('Copy Markdown')}
                      </Button>
                    )}
                  </CopyButton>
                </Group>

                <Box p="md">
                  <Code block>{markdownSnippet}</Code>
                </Box>
              </Paper>

              <Paper withBorder radius="md" p="md">
                <Group align="flex-start" gap="sm" wrap="nowrap">
                  <ThemeIcon variant="light" color="blue" radius="xl">
                    <Info size={16} />
                  </ThemeIcon>
                  <Stack gap={2}>
                    <Text fw={700}>{t('Pro Tip')}</Text>
                    <Text c="dimmed" size="sm">
                      {t('When users click these badges on your GitHub page, they will be redirected to your public cryptographic verification ledger, proving your undeniable prior art ownership.')}
                    </Text>
                  </Stack>
                </Group>
              </Paper>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default BadgeSetupWidget;
